import { IllegalStateError, UnsupportedOperationError } from "@archipad-js/core/error";
import { BaseEntity, toBaseEntityInternal } from "./base-entity";
import { EntityContext } from "./entity-context";
import { DynamicEntityConstructor, EntityType, EntityTypes } from "./types";
import { ORMError } from "./errors";

export class DynamicEntities {
    private _dynamicEntityByName: Record<string, DynamicEntityConstructor>;

    constructor(
        private readonly entityTypes: EntityTypes,
        private readonly entityClasses: ReadonlyMap<string, typeof BaseEntity>,
    ) {
        this.buildDynamicEntities();
    }

    private get byIndex(): ReadonlyArray<EntityType> {
        return this.entityTypes.byIndex;
    }

    private buildDynamicEntities(): void {
        this._dynamicEntityByName = {};

        // pass : create the dynamic entity
        for (const entityType of this.byIndex) {
            // create the dynamic entity (which is the entity constructor function)
            const dynamicEntity: DynamicEntityConstructor = this.createDynamicEntity(entityType.entityName, entityType);

            // add entity to the collection
            this._dynamicEntityByName[entityType.entityName] = dynamicEntity;
        }

        // pass : build calculated properties metadata
        for (const entityType of this.byIndex) {
            for (const propertyName in entityType.metadata) {
                const property = entityType.metadata[propertyName];

                const dynamicEntity: DynamicEntityConstructor = this.dynamicEntityByName[entityType.entityName];
                const defined = isCalculatedPropertyDefined(dynamicEntity.prototype, property.name);
                if (property.calculated) {
                    if (!defined) {
                        throw new IllegalStateError(
                            `Calculated property '${property.name}' is not defined in '${entityType.entityName}' entity`,
                        );
                    }
                }

                // Check if the relation is a calculated property and if it has an inverse relation
                if (defined && property.relation === true && !property.isInverse && property.inverseField) {
                    /**
                     * Calculated relations with inverse relation are not supported for now.
                     * This is because we don't find any use case for this.
                     * @link [AP-8150](https://bigsool-archipad.atlassian.net/browse/AP-8150)
                     */
                    throw new IllegalStateError(
                        `Calculated property '${property.name}' cannot have an inverse relation`,
                    );
                }
            }
        }

        // pass : define properties (attributes and relations)
        for (const entityType of this.byIndex) {
            const dynamicEntity: DynamicEntityConstructor = this.dynamicEntityByName[entityType.entityName];

            for (const propertyName in entityType.metadata) {
                const property = entityType.metadata[propertyName];

                // define attributes
                if (property.relation === false) {
                    if (property.primaryKey) {
                        const propertyDescriptor: PropertyDescriptor = {
                            enumerable: true,
                            get: function (this: BaseEntity): string {
                                return toBaseEntityInternal(this).state.data["id"] as string;
                            },
                            set: function (this: BaseEntity, v: string): void {
                                throw new UnsupportedOperationError("The modification of an entity id is forbidden.");
                            },
                        };

                        defineDynamicPropertyValueFromDescriptor(
                            entityType,
                            property.name,
                            dynamicEntity,
                            propertyDescriptor,
                        );
                    } else {
                        defineDynamicValueProperty(entityType, property.name, dynamicEntity);
                    }
                }
                // define relations
                else {
                    defineDynamicRelation(entityType, property.name, dynamicEntity);
                }
            }
        }
    }

    private createDynamicEntity(entityName: string, entityType: EntityType): DynamicEntityConstructor {
        // load entity type base class if there is one
        const EntityClass = this.entityClasses.get(entityName) ?? BaseEntity;

        const DynamicEntity = class DynamicEntity extends EntityClass {
            constructor(entityContext: EntityContext, entityId: string) {
                super(entityType, entityContext);
                toBaseEntityInternal(this).state.data.id = entityId;
                Object.seal(this);
            }
        };

        return DynamicEntity;
    }

    public get dynamicEntityByName(): Readonly<Record<string, DynamicEntityConstructor>> {
        return this._dynamicEntityByName;
    }
}

function isCalculatedPropertyDefined(dynamicEntity: BaseEntity, name: string): boolean {
    // find a getter property defined in an entity extension `XxxEntity-ext.ts`
    const property = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(dynamicEntity), name);
    return Boolean(property);
}

function defineGetter(property: PropertyDescriptor, name: string) {
    property.get = function (this: BaseEntity) {
        return toBaseEntityInternal(this).state.data[name];
    };
}

// TODO implement optional
function defineSetter(property: PropertyDescriptor, name: string) {
    property.set = function (this: BaseEntity, val: unknown) {
        if (val instanceof BaseEntity) {
            if (this.getContext() !== null && this.getContext() !== val.getContext()) {
                const error = new ORMError(
                    "Illegal attempt to make a relation between two entities from different EntityContexts",
                );
                error.addContext("Archipad", {
                    "source entity": {
                        entityName: this.entityType.entityName,
                        entityId: this.id,
                        propertyName: name,
                    },
                });
                error.addContext("Archipad", {
                    "target entity": {
                        entityName: val.entityType.entityName,
                        entityId: val.id,
                    },
                });
                throw error;
            }
        }

        const oldVal = toBaseEntityInternal(this).state.data[name];
        if (val !== oldVal) {
            toBaseEntityInternal(this).state.data[name] = val ?? null;
            if (!this.entityContext) {
                throw new ORMError("Illegal assignment to a deleted orm object", this.entityType.entityName, this.id);
            }
            if (this.entityContext) {
                this.entityContext.entityUpdated(this, name, oldVal, val);
            }
        }
    };
}

function defineDynamicProperty(entityType: EntityType, name: string, dynamicEntity: DynamicEntityConstructor): void {
    const metadata = entityType.metadata[name];
    if (metadata.calculated) {
        return;
    }

    const property: PropertyDescriptor = { enumerable: true };
    defineGetter(property, name);
    defineSetter(property, name);

    Object.defineProperty(dynamicEntity.prototype, name, property);
}

function defineDynamicPropertyValueFromDescriptor(
    entityType: EntityType,
    name: string,
    dynamicEntity: DynamicEntityConstructor,
    property: PropertyDescriptor,
): void {
    Object.defineProperty(dynamicEntity.prototype, name, property);
}

// aliases
const defineDynamicValueProperty = defineDynamicProperty;
const defineDynamicRelation = defineDynamicProperty;
